import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import ModalLayout from '../layouts/ModalLayout';
import StepFlow, { StepFlowStep } from '../misc/StepFlow';
import calendarConnectionService from '../../../services/community/calendarConnectionService';
import { EventType, NotificationSettings } from '../../../types/event';
import { ConnectionIdentifier } from '../../../types/misc';
import { RootState } from '../../../redux/reducers';
import IconBadge from '../icons/IconBadge';
import { eventTypeIcons } from '../../../utils/iconUtils';
import EventNotificationForm from '../forms/EventNotificationForm';
import ParticipantsPicker, { SelectOption } from '../misc/ParticipantsPicker';
import eventUtils from '../../../utils/eventUtils';

interface AddExternalCalendarModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCalendarAdded: () => void;
}

interface CalendarSetupViewProps {
  setStep: (step: 1 | -1) => void;
  calendarUrl: string;
  setCalendarUrl: React.Dispatch<React.SetStateAction<string>>;
  selectedEventType: EventType | undefined;
  setSelectedEventType: React.Dispatch<React.SetStateAction<EventType | undefined>>;
}

interface ConfigurationViewProps {
  setStep: (step: 1 | -1) => void;
  calendarUrl: string;
  selectedEventType: EventType | undefined;
  notificationSettings: NotificationSettings;
  setNotificationSettings: React.Dispatch<React.SetStateAction<NotificationSettings>>;
  selectedParticipants: ConnectionIdentifier[];
  setSelectedParticipants: React.Dispatch<React.SetStateAction<ConnectionIdentifier[]>>;
  onCalendarAdded: () => void;
  setOpen: (open: boolean) => void;
}

export default function AddExternalCalendarModal({
  open,
  setOpen,
  onCalendarAdded,
}: AddExternalCalendarModalProps): JSX.Element {
  const { t } = useTranslation();
  const [step, setStep] = useState<number>(0);
  const [calendarUrl, setCalendarUrl] = useState('');
  const [selectedEventType, setSelectedEventType] = useState<EventType | undefined>(undefined);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(
    eventUtils.getMediumNotifications(false)
  );
  const [selectedParticipants, setSelectedParticipants] = useState<ConnectionIdentifier[]>([]);

  const handleStep = (diff: 1 | -1) => setStep((prev) => prev + diff);

  const resetForm = () => {
    setStep(0);
    setCalendarUrl('');
    setSelectedEventType(undefined);
    setNotificationSettings(eventUtils.getMediumNotifications(false));
    setSelectedParticipants([]);
  };

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const calendarSetupProps: CalendarSetupViewProps = {
    setStep: handleStep,
    calendarUrl,
    setCalendarUrl,
    selectedEventType,
    setSelectedEventType
  };

  const configurationProps: ConfigurationViewProps = {
    setStep: handleStep,
    calendarUrl,
    selectedEventType,
    notificationSettings,
    setNotificationSettings,
    selectedParticipants,
    setSelectedParticipants,
    onCalendarAdded,
    setOpen
  };

  const steps = [
    <CalendarSetupView {...calendarSetupProps} />,
    <ConfigurationView {...configurationProps} />,
  ];

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="max-w-4xl w-full mx-4 rounded-xl bg-secondary-50 p-6">
      <div className="flex items-center gap-3 mb-6">
        <FaPlus className="h-6 w-6 text-primary-500" />
        <h2 className="text-2xl font-semibold text-primary-900">
          {t('page.event.calendar.external.addCalendar')}
        </h2>
      </div>

      <div className="max-h-[70vh] overflow-y-auto">
        <StepFlow
          indexState={[step, setStep]}
          steps={steps}
          className='p-0'
        />
      </div>
    </ModalLayout>
  );
}

function CalendarSetupView({
  setStep,
  calendarUrl,
  setCalendarUrl,
  selectedEventType,
  setSelectedEventType
}: CalendarSetupViewProps): JSX.Element {
  const { t } = useTranslation();

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCalendarUrl(e.target.value);
  };

  return (
    <StepFlowStep
      title={t('page.event.calendar.external.step1.title')}
      subtitle={t('page.event.calendar.external.step1.subtitle')}
      setStep={setStep}
      nextText={t('general.next')}
      showPrev={false}
      disabled={!calendarUrl.trim() || !selectedEventType}
      className='max-w-full'
    >
      <div className="flex flex-col gap-6">
        {/* Calendar URL Input */}
        <div className="flex flex-col gap-3">
          <label className="text-lg font-medium text-primary-900">
            {t('page.event.calendar.external.calendarUrl')}
          </label>
          <input
            type="url"
            value={calendarUrl}
            onChange={handleUrlChange}
            placeholder="https://calendar.google.com/calendar/ical/..."
            className="rounded-lg border border-secondary-200 px-4 py-3 text-primary-900 focus:border-primary-300 focus:outline-none text-lg"
          />
        </div>

        {/* Event Type Selection */}
        <div className="flex flex-col gap-3">
          <label className="text-lg font-medium text-primary-900">
            {t('page.createEvent.type.title')}
          </label>
          <div className="flex flex-col gap-2">
            {Object.values(EventType).map((type) => (
              <div
                key={type}
                onClick={() => setSelectedEventType(type === selectedEventType ? undefined : type)}
                className={classNames(
                  'flex w-full cursor-pointer justify-between gap-2 rounded-[8px] border border-secondary-200 px-4 py-3 transition-all hover:bg-secondary-100',
                  { 'bg-secondary-200': selectedEventType === type },
                )}>
                <IconBadge icon={eventTypeIcons[type]} className="rounded-[6px] p-1" />
                <div className="flex w-full items-center justify-between">
                  <h3 className="text-lg">
                    {t(`page.createEvent.type.type.${type.toLowerCase()}.title`)}
                  </h3>
                  <input type="checkbox" checked={selectedEventType === type} readOnly />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </StepFlowStep>
  );
}

function ConfigurationView({
  setStep,
  calendarUrl,
  selectedEventType,
  notificationSettings,
  setNotificationSettings,
  selectedParticipants,
  setSelectedParticipants,
  onCalendarAdded,
  setOpen
}: ConfigurationViewProps): JSX.Element {
  const { t } = useTranslation();
  const [addingCalendar, setAddingCalendar] = useState(false);
  const [submitText, setSubmitText] = useState<string>(t('general.add'));
  const [selectOption, setSelectOption] = useState<SelectOption>('active');

  const connections = useSelector((state: RootState) => state.connections);

  useEffect(() => {
    if (connections.length > 0 && selectedParticipants.length === 0) {
      setSelectedParticipants(connections);
    }
  }, [connections, selectedParticipants.length, setSelectedParticipants]);

  const handleAddCalendar = async () => {
    if (!selectedEventType) {
      toast.error(t('page.event.calendar.external.eventTypeRequired'));
      return;
    }

    try {
      setAddingCalendar(true);
      setSubmitText(t('general.adding'));

      const toInvite = selectedParticipants.filter(c => c.userId !== undefined).map(c => +(c.userId!));
      const toInviteExternal = selectedParticipants.filter(c => c.externalMemberId !== undefined && c.externalMemberId !== null).map(c => ({ externalMemberId: c.externalMemberId! }));
      const inviteAll = selectOption === 'all';
      const inviteActive = selectOption === 'active';

      await calendarConnectionService.createExternalCalendar(
        calendarUrl.trim(),
        selectedEventType,
        notificationSettings,
        toInvite,
        toInviteExternal,
        inviteAll,
        inviteActive
      );

      toast.success(t('page.event.calendar.external.addSuccess'));
      setOpen(false);
      onCalendarAdded();
    } catch (error) {
      toast.error(t('page.event.calendar.external.addError'));
      setAddingCalendar(false);
      setSubmitText(t('general.add'));
    }
  };

  return (
    <StepFlowStep
      title={t('page.event.calendar.external.step2.title') || t('general.configuration')}
      subtitle={t('page.event.calendar.external.step2.subtitle') || t('page.event.calendar.external.step2.subtitle')}
      setStep={setStep}
      showNext={false}
      onSubmit={handleAddCalendar}
      submitText={submitText}
      disabled={addingCalendar || !selectedEventType}
      className='max-w-full'
    >
      <div className="flex flex-col gap-6">
        {/* Notification Settings */}
        <div className="flex flex-col gap-3">
          <label className="text-lg font-medium text-primary-900">
            {t(`form.notificationSettings.title`)}
          </label>
          <EventNotificationForm
            value={notificationSettings}
            onChange={setNotificationSettings}
            isDatePicker={false}
            hideTitle
          />
        </div>

        {/* Participants Selection */}
        <div className="flex flex-col gap-3">
          <label className="text-lg font-medium text-primary-900">
            {t('page.createEvent.participants.title')}
          </label>
          <p className="text-sm text-primary-600 mb-2">
            {t('page.event.calendar.external.participantsDescription')}
          </p>
          <ParticipantsPicker
            selected={selectedParticipants}
            disable={(c) => !c.fields.EMAIL}
            setSelected={setSelectedParticipants}
            selectOption={selectOption}
            setSelectOption={setSelectOption}
          />
        </div>
      </div>
    </StepFlowStep>
  );
}
